// TRT Follow-up Questionnaire (testosterone cypionate, cream, spray, enclomiphene, anastrozole)
//
// Source of truth: Beluga Health "Comprehensive TRT Follow Up Questionnaire" —
// see docs/Comprehensive TRT Follow Up Questionnaire.docx (2026-05-25 drop).
// Question order, conditional branching, scoring, and consent body all come
// from that doc. PURPLE-marked questions (medicalConditions, selfReportedMeds,
// allergies) are extracted by the backend mapper to top-level named fields
// and dropped from the Q/A array.
//
// Key differences vs trt intake (src/data/trtData.js):
//   - No LTRS panel, no disqualifying-conditions screen, no new-vs-continuation
//     branch, no sex-at-birth / fertility-planning re-ask (locked in at intake).
//   - All 10 qADAM slides with the "Since your last visit, …" prefix.
//   - NEW slides: acute-symptoms (hard halt with EMS message), testicular-changes
//     (PINK routes to HCG once Beluga adds HCG to fitpal catalog).
//   - Lab routing is implicit: belugaOrdersLabs=true is hardcoded by the
//     submit envelope (Beluga requires fresh labs every 3 months). No
//     lab-availability slide on the followup.
//   - Same consent body as intake; patient re-consents at every followup.
//
// Entry point: only via the dashboard embed wrapper (?embed=1&type=trt).
// FollowUpForm.jsx redirects direct traffic to the dashboard login.

// ---------- Shared option constants ----------

const QADAM_5 = [
  { label: 'Terrible', value: 'terrible', points: 1 },
  { label: 'Poor', value: 'poor', points: 2 },
  { label: 'Average', value: 'average', points: 3 },
  { label: 'Good', value: 'good', points: 4 },
  { label: 'Excellent', value: 'excellent', points: 5 },
];

const TRT_FOLLOWUP_MAIN_CONSENT_BODY = `TRT, HCG, and SERM-Based Therapy Informed Consent

You are requesting evaluation and possible treatment for symptoms related to low testosterone. Treatment options may include:
- Testosterone Replacement Therapy (TRT)
- Human Chorionic Gonadotropin (HCG)
- Selective Estrogen Receptor Modulators (SERMs) such as clomiphene or enclomiphene
- Aromatase inhibitors (AIs) when clinically appropriate

These therapies may be used alone or in combination depending on your symptoms, laboratory results, and treatment goals (including fertility preservation).

TRT may be administered as an injection, gel, oral regimen, cream, or nasal formulation. HCG, SERMs, and AIs are oral or injectable therapies used to stimulate or regulate endogenous hormone production. All treatment is prescribed and monitored by a licensed healthcare provider.

POTENTIAL BENEFITS
Treatment may improve:
- Energy, libido, and sexual function
- Muscle mass, strength, and exercise tolerance
- Mood and overall well-being
HCG and SERMs may help stimulate natural testosterone production and support fertility in some patients.

RISKS AND SIDE EFFECTS

TRT:
- Acne, oily skin
- Fluid retention
- Mood changes
- Testicular shrinkage and reduced sperm production
- Elevated red blood cell count

HCG:
- Injection site irritation
- Headache or mood changes
- Gynecomastia
- Increased estrogen levels

Clomiphene / Enclomiphene (SERMs):
- Headache
- Mood changes
- Visual disturbances (rare but important)
- Possible liver or lipid changes

Aromatase Inhibitors (AIs):
- Joint pain
- Fatigue
- Low estrogen symptoms (e.g., mood or libido changes)
- Decreased bone density with long-term use

SERIOUS (RARE) RISKS:
- Elevated hematocrit
- Cardiovascular events (clot, stroke, heart attack)
- Worsening sleep apnea
- Liver abnormalities
- Allergic reactions

Seek emergency care for chest pain, shortness of breath, severe headache, or leg swelling.

FERTILITY CONSIDERATIONS
- TRT may suppress sperm production and lead to infertility
- HCG and SERMs may help preserve or stimulate fertility, but this is not guaranteed

COMPOUNDED MEDICATIONS
Some therapies may be compounded and not FDA-approved. These may be used when clinically appropriate; however, comparative safety and efficacy data may be limited.

CONTRAINDICATIONS
You must disclose any history of:
- Prostate or breast cancer
- Polycythemia
- Severe heart disease or recent stroke
- Untreated sleep apnea
- Allergy to any prescribed therapy

MONITORING REQUIREMENTS
Ongoing treatment requires periodic labs (typically every 3-6 months), which may include:
- CBC (hematocrit)
- Testosterone levels
- PSA (when appropriate)
- Liver function and lipids
- Estradiol (when indicated)
Failure to complete required monitoring may result in dose adjustment or discontinuation.

ALTERNATIVES
- No treatment
- Lifestyle optimization (sleep, exercise, weight loss)
- Non-hormonal therapies
- HCG or SERMs alone (fertility-focused approaches)

TRUTHFULNESS AND ACCURACY OF INFORMATION
I acknowledge that the safety and appropriateness of my treatment depend on the accuracy and completeness of the information I provide. I confirm that:
- All medical history, medications, allergies, and symptoms I have provided are true, accurate, and complete to the best of my knowledge
- I will update my provider if there are any changes to my health, medications, or symptoms
- I understand that failure to provide accurate or complete information may increase my risk of adverse outcomes and may result in discontinuation of care

ACKNOWLEDGMENT AND CONSENT
I understand:
- The risks, benefits, and alternatives of TRT, HCG, SERMs, and AIs
- No outcomes are guaranteed
- Ongoing monitoring is required
- My treatment may be modified or discontinued for safety
I consent to evaluation and treatment under medical supervision.`;

// ---------- PURPLE textarea slides (extracted to named fields) ----------

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Hypertension, Type 2 Diabetes, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Lisinopril 10mg daily, Atorvastatin 20mg daily…',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (rash), Shellfish (anaphylaxis)…',
  required: true,
};

// ---------- Height / weight ----------

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'We use this to track changes since your last visit.',
  // No BMI gate on followup — provider reviews against trends.
  skipBmiCheck: true,
};

// ---------- Health changes ----------

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question:
    'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes…',
  required: true,
};

// ---------- Acute symptoms safety gate (NON-blocking — EMS modal) ----------
//
// Per the canonical BE PRD (2026-05-25): acute-symptoms=Yes is NOT a hard
// disqualifier. The FE renders an EMS warning modal with two CTAs:
//   1. "Call 911"            → tel:911 (primary, red)
//   2. "Continue with my visit anyway" → advances form (secondary)
// Backend does NOT reject the visit — provider review still happens; the FE
// just elevates urgency. The Yes option carries `displayEmsWarning: true`
// instead of disqualifies/disqualifyReason. FollowUpForm.jsx handleAnswer
// intercepts this and pops the modal in place of auto-advance.

const acuteSymptomsQuestion = {
  id: 'acute-symptoms',
  type: 'single-select',
  question:
    'In the past 72 hours, have you experienced any of the following: chest pain or shortness of breath; severe dizziness or fainting; facial swelling, rash, or hives; thoughts of self-harm or feeling unsafe?',
  options: [
    { label: 'No', value: 'no' },
    {
      label: 'Yes',
      value: 'yes',
      // Docx-verbatim EMS message — rendered inside the modal body.
      displayEmsWarning: true,
      emsWarningMessage:
        'These are concerning symptoms that should prompt immediate medical evaluation. Please contact EMS services immediately for further evaluation.',
    },
  ],
};

// ---------- Side effects ----------

const sideEffectsQuestion = {
  id: 'medication-side-effects',
  type: 'single-select',
  question: 'Have you experienced any side effects from the current medication?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'medication-side-effects-details',
  type: 'textarea',
  question: 'Please tell us more about the side effects you are experiencing',
  showIf: { field: 'medication-side-effects', value: 'yes' },
  placeholder: 'Describe the side effects…',
  required: true,
};

// ---------- Testicular changes (PINK → HCG routing, v1 log-only) ----------

const testicularChangesQuestion = {
  id: 'testicular-changes',
  type: 'single-select',
  question: 'Are you noticing a decrease in testicular size or fullness?',
  // PINK marker — backend logs the answer for clinical review. v1 does NOT
  // auto-inject HCG into patientPreference (no HCG SKU in fitpal catalog
  // yet — Beluga catalog gap, see BACKEND_TRT_PLAN_SEED_BLOCKER_PRD §3 Q1).
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

// ---------- Last dose date ----------

const lastDoseDateQuestion = {
  id: 'last-dose-date',
  type: 'textarea',
  question:
    'Please enter the exact date you last administered your testosterone dose.',
  subtitle:
    'Accurate timing helps us safely determine your next scheduled dose.',
  placeholder: 'e.g., 05/24/2026',
  required: true,
};

// ---------- Dose adjusted ----------

const doseAdjustedQuestion = {
  id: 'dose-adjusted',
  type: 'single-select',
  question:
    'Has your dose been adjusted by another provider or have you self adjusted your dose since your last visit?',
  options: [
    { label: 'Yes, this has been adjusted', value: 'yes' },
    {
      label:
        "No, I'm taking the same dose that was prescribed during my last appointment",
      value: 'no',
    },
  ],
};

const doseAdjustedDetailsQuestion = {
  id: 'dose-adjusted-details',
  type: 'textarea',
  question:
    'Please provide more information about who adjusted your dose, why this was adjusted, and what is your current dose?',
  showIf: { field: 'dose-adjusted', value: 'yes' },
  placeholder: 'Describe who adjusted, why, and current dose…',
  required: true,
};

// ---------- qADAM panel (10 slides) ----------
//
// All slides prefixed with "Since your last visit, …" per docx — distinct
// from the intake's qADAM phrasing. Backend's qadamTotal computation reads
// the option.points field; backend re-verifies the sum.

const qadamLibido = {
  id: 'qadam-libido',
  type: 'single-select',
  question: 'Since your last visit, how would you rate your libido (sex drive)?',
  options: QADAM_5,
};

const qadamEnergy = {
  id: 'qadam-energy',
  type: 'single-select',
  question: 'Since your last visit, how would you rate your energy level?',
  options: QADAM_5,
};

const qadamStrengthEndurance = {
  id: 'qadam-strength-endurance',
  type: 'single-select',
  question:
    'Since your last visit, how would you rate your strength or endurance?',
  options: QADAM_5,
};

const qadamEnjoyment = {
  id: 'qadam-enjoyment',
  type: 'single-select',
  question: 'Since your last visit, how would you rate your enjoyment of life?',
  options: QADAM_5,
};

const qadamHappiness = {
  id: 'qadam-happiness',
  type: 'single-select',
  question:
    'Since your last visit, how would you rate your overall happiness or mood?',
  options: QADAM_5,
};

const qadamErections = {
  id: 'qadam-erections',
  type: 'single-select',
  question: 'Since your last visit, how strong are your erections?',
  options: [
    { label: 'Extremely weak', value: 'extremely-weak', points: 1 },
    { label: 'Weak', value: 'weak', points: 2 },
    { label: 'Moderate', value: 'moderate', points: 3 },
    { label: 'Strong', value: 'strong', points: 4 },
    { label: 'Extremely strong', value: 'extremely-strong', points: 5 },
  ],
};

const qadamWorkPerformance = {
  id: 'qadam-work-performance',
  type: 'single-select',
  question:
    'Since your last visit, how would you rate your work performance over the past 4 weeks?',
  options: QADAM_5,
};

const qadamFallAsleepAfterDinner = {
  id: 'qadam-fall-asleep-after-dinner',
  type: 'single-select',
  question: 'Since your last visit, how often do you fall asleep after dinner?',
  options: [
    { label: 'Never', value: 'never', points: 1 },
    { label: '1-2 times per week', value: '1-to-2', points: 2 },
    { label: '3-4 times per week', value: '3-to-4', points: 3 },
    { label: '5-6 times per week', value: '5-to-6', points: 4 },
    { label: 'Every night', value: 'every-night', points: 5 },
  ],
};

const qadamSportsPerformance = {
  id: 'qadam-sports-performance',
  type: 'single-select',
  question:
    'Since your last visit, how would you rate your sports or physical performance over the past 4 weeks?',
  options: QADAM_5,
};

const qadamHeightLoss = {
  id: 'qadam-height-loss',
  type: 'single-select',
  question: 'How much height have you lost?',
  options: [
    { label: '2 inches or more', value: '2-or-more', points: 1 },
    { label: '1.5-1.9 inches', value: '1.5-to-1.9', points: 2 },
    { label: '1-1.4 inches', value: '1-to-1.4', points: 3 },
    { label: '0.5-0.9 inches', value: '0.5-to-0.9', points: 4 },
    { label: 'None (0-0.4 inches)', value: 'none', points: 5 },
  ],
};

// ---------- Additional questions + consent ----------

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here…',
  required: false,
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'TRT, HCG, and SERM-Based Therapy Informed Consent',
  consentText: TRT_FOLLOWUP_MAIN_CONSENT_BODY,
  options: [
    {
      label:
        'I have read the above information and I do consent and wish to move forward',
      value: 'consent',
    },
    {
      label: 'No — I do not wish to continue with treatment',
      value: 'decline',
      disqualifies: true,
    },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

// ---------- Question registry ----------

export const trtFollowUpQuestions = {
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  heightWeight: heightWeightQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  acuteSymptoms: acuteSymptomsQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  testicularChanges: testicularChangesQuestion,
  lastDoseDate: lastDoseDateQuestion,
  doseAdjusted: doseAdjustedQuestion,
  doseAdjustedDetails: doseAdjustedDetailsQuestion,
  qadamLibido,
  qadamEnergy,
  qadamStrengthEndurance,
  qadamEnjoyment,
  qadamHappiness,
  qadamErections,
  qadamWorkPerformance,
  qadamFallAsleepAfterDinner,
  qadamSportsPerformance,
  qadamHeightLoss,
  additionalQuestions: additionalQuestionsQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

// Flow order matches the Beluga source-of-truth docx top-to-bottom. PURPLE
// fields (medical-conditions, self-reported-meds, allergies) sit at the very
// top per docx; backend mapper extracts them to named fields and drops the
// Q/A entries.
export const trtFollowUpFlow = [
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'height-weight',
  'health-changes',
  'health-changes-details',
  'acute-symptoms',
  'medication-side-effects',
  'medication-side-effects-details',
  'testicular-changes',
  'last-dose-date',
  'dose-adjusted',
  'dose-adjusted-details',
  'qadam-libido',
  'qadam-energy',
  'qadam-strength-endurance',
  'qadam-enjoyment',
  'qadam-happiness',
  'qadam-erections',
  'qadam-work-performance',
  'qadam-fall-asleep-after-dinner',
  'qadam-sports-performance',
  'qadam-height-loss',
  'additional-questions',
  'main-consent',
  'checkout',
];

// Slide IDs grouped for qADAM scoring helper
export const TRT_FOLLOWUP_QADAM_SLIDE_IDS = [
  'qadam-libido',
  'qadam-energy',
  'qadam-strength-endurance',
  'qadam-enjoyment',
  'qadam-happiness',
  'qadam-erections',
  'qadam-work-performance',
  'qadam-fall-asleep-after-dinner',
  'qadam-sports-performance',
  'qadam-height-loss',
];

export const getTrtFollowUpQuestionById = (id) =>
  Object.values(trtFollowUpQuestions).find((q) => q.id === id);

// Builds the visible flow given current answers. Mirrors
// buildDynamicAntiAgingFollowUpFlow but for the TRT followup question set.
// Three BLUE conditionals fire on their preceding single-select:
//   health-changes-details   ← health-changes='yes'
//   medication-side-effects-details     ← side-effects='yes'
//   dose-adjusted-details    ← dose-adjusted='yes'
export const buildDynamicTrtFollowUpFlow = (answers /* , userData */) => {
  const flow = [];
  for (const questionId of trtFollowUpFlow) {
    const question = getTrtFollowUpQuestionById(questionId);
    if (!question) continue;

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    flow.push(question);
  }
  return flow;
};

// Compute qADAM total (10-50) from the answers map. Backend re-verifies.
export const computeTrtFollowUpQadam = (answers) => {
  let total = 0;
  for (const slideId of TRT_FOLLOWUP_QADAM_SLIDE_IDS) {
    const q = getTrtFollowUpQuestionById(slideId);
    if (!q || !Array.isArray(q.options)) continue;
    const ans = answers[slideId];
    const opt = q.options.find((o) => o.value === ans);
    if (opt && typeof opt.points === 'number') total += opt.points;
  }
  return total;
};

export default trtFollowUpQuestions;
